import { EventTrackingActions } from '@campus/ab-test';
import { ContextInterface, DalState, EduContent, EduContentInterface } from '@campus/dal';
import { Store } from '@ngrx/store';
import {
  ContentActionHandlerFunctionType,
  ContentActionHandlerParamsType,
  ContentActionInterface,
} from '../content-action.interface';
import {
  ContentActionDictionary,
  ContentActionType,
  ContentOpenerInterface,
  OpenContentOptions,
} from './content-open-actions.service.interface';
/**
 * Base service for creating content actions with bound handlers.
 *
 * This service provides the core functionality for creating content action objects
 * that include executable handlers. It implements a factory pattern where:
 *
 * 1. Action definitions are retrieved from a dictionary
 * 2. Handlers are created by binding content opener methods with preset arguments
 * 3. Special handling is provided for different action types (e.g., review actions)
 * 4. A fallback system ensures handlers are available even if primary openers fail
 *
 * The service uses a two-tier opener system:
 * - Primary opener: Module-specific implementations with enhanced functionality
 * - Default opener: Fallback implementation providing basic functionality
 *
 * Handler binding allows for partial application of arguments, where preset arguments
 * (like content and base options) are combined with runtime arguments when the handler
 * is actually executed.
 *
 *
 * @abstract This class is designed to be extended by concrete implementations
 * that provide specific action dictionaries and opener configurations.
 */
export class ContentOpenActionsBaseService {
  private static readonly REVIEW_ACTION_TYPE = 'openEduContentForReview' as const;
  /**
   * Creates a new content actions service.
   *
   * @param contentOpener - Primary content opener (module-specific implementation)
   * @param defaultContentOpener - Fallback content opener (basic implementation)
   * @param contentActionDictionary - Available action definitions mapped by type
   */
  constructor(
    protected readonly contentOpener: ContentOpenerInterface,
    protected readonly defaultContentOpener: ContentOpenerInterface,
    public readonly contentActionDictionary: ContentActionDictionary,
    public readonly store: Store<DalState>
  ) {}

  protected createContentActionWithHandler(
    actionType: ContentActionType,
    handlerArguments: ContentActionHandlerParamsType
  ): ContentActionInterface | undefined {
    const baseAction = this.contentActionDictionary[actionType];

    if (!baseAction) {
      console.warn(`No action found for type: ${actionType}`);
      return undefined;
    }

    return this.enrichActionWithHandler(baseAction, handlerArguments, actionType);
  }

  /**
   * Enriches a base action definition with a bound handler function.
   *
   * Takes a basic action definition (label, icon, etc.) and adds an executable
   * handler function that's bound to the appropriate content opener method.
   *
   * @param contentAction - Base action definition without handler
   * @param handlerArguments - Arguments to bind to the handler
   * @returns Complete action with executable handler
   */
  private enrichActionWithHandler(
    contentAction: ContentActionInterface,
    handlerArguments: ContentActionHandlerParamsType,
    actionType: ContentActionType
  ): ContentActionInterface {
    const processedArguments = this.processHandlerArguments(contentAction, handlerArguments);

    return {
      ...contentAction,
      handler: this.createHandler(contentAction.handlerMethod, actionType, processedArguments),
    };
  }

  /**
   * Processes handler arguments based on action type requirements.
   *
   * Different action types have different argument processing needs:
   * - Review actions: Preserve all arguments including forResult context
   * - Other actions: Remove forResult to prevent unintended result associations
   *
   * @param contentAction - The action being processed
   * @param handlerArguments - Raw arguments to process
   * @returns Processed arguments appropriate for the action type
   */
  private processHandlerArguments(
    contentAction: ContentActionInterface,
    handlerArguments: ContentActionHandlerParamsType
  ): ContentActionHandlerParamsType {
    // Only review actions should retain the forResult option
    if (contentAction.type === ContentOpenActionsBaseService.REVIEW_ACTION_TYPE) {
      return handlerArguments;
    }

    const [content, options, ...rest] = handlerArguments;
    const processedOptions = { ...options, forResult: undefined };

    return [content, processedOptions, ...rest];
  }

  /**
   * Creates a handler function by finding the appropriate content opener method
   * and binding it with the provided arguments.
   *
   * The handler creation process:
   * 1. Find the appropriate opener and method (primary or fallback)
   * 2. Validate that the method exists
   * 3. Create a bound handler that preserves context and merges arguments
   *
   * @param methodName - Name of the content opener method to bind
   * @param handlerArguments - Arguments to bind to the method
   * @returns Bound handler function, or undefined if method not found
   */
  private createHandler(
    methodName: ContentActionInterface['handlerMethod'],
    actionType: ContentActionType,
    handlerArguments?: ContentActionHandlerParamsType
  ): ContentActionHandlerFunctionType | undefined {
    const { opener, handler } = this.findOpenerAndHandler(methodName);

    if (!handler) {
      console.warn(`No handler found for method: ${methodName}`);
      return undefined;
    }

    return this.createBoundHandler(handler, opener, actionType, handlerArguments);
  }

  /**
   * Locates the appropriate content opener and method using fallback strategy.
   *
   * Implements a two-tier system:
   * 1. Try the primary content opener first (module-specific functionality)
   * 2. Fall back to default content opener (basic functionality)
   *
   * This allows modules to provide enhanced implementations while ensuring
   * that basic functionality is always available.
   *
   * @param methodName - Name of the method to find
   * @returns Object containing the opener instance and method function, or nulls if not found
   *
   */
  private findOpenerAndHandler(methodName: string) {
    // Try primary opener first
    if (this.contentOpener?.[methodName]) {
      return {
        opener: this.contentOpener,
        handler: this.contentOpener[methodName],
      };
    }

    // Fall back to default opener
    if (this.defaultContentOpener?.[methodName]) {
      return {
        opener: this.defaultContentOpener,
        handler: this.defaultContentOpener[methodName],
      };
    }

    return { opener: null, handler: null };
  }

  /**
   * Creates a bound handler function that merges preset and runtime arguments.
   *
   * The bound handler implements partial application, where:
   * - Preset arguments are captured at binding time
   * - Runtime arguments are provided when the handler is executed
   * - Runtime arguments take precedence over preset ones for options
   * - Content can be overridden at runtime or falls back to preset content
   *
   * This pattern allows for flexible handler reuse where the same action
   * can be configured once but executed with different runtime contexts.
   *
   * @param originalHandler - The original content opener method
   * @param opener - The opener instance to use as 'this' context
   * @param presetArguments - Arguments bound at creation time
   * @returns New function that merges preset and runtime arguments
   *
   * @example
   * ```typescript
   * // Create handler with preset arguments
   * const handler = this.createBoundHandler(
   *   opener.openEduContentAsExercise,
   *   opener,
   *   [eduContent, { bookId: 123 }]
   * );
   *
   * // Execute with runtime arguments (merges options, overrides content if provided)
   * handler(newContent, { tocId: 456 }); // Results in: { bookId: 123, tocId: 456 }
   * ```
   */
  private createBoundHandler(
    originalHandler: ContentActionHandlerFunctionType,
    opener: ContentOpenerInterface,
    actionType: ContentActionType,
    presetArguments?: ContentActionHandlerParamsType
  ): ContentActionHandlerFunctionType {
    if (!presetArguments) {
      return originalHandler;
    }

    return (
      runtimeContent?: EduContent,
      runtimeOptions?: OpenContentOptions,
      runTimeContext?: ContextInterface,
      ...extraArgs: any[]
    ) => {
      const [presetContent, presetOptions = {}, presetContext = {}] = presetArguments;

      // Runtime arguments take precedence over preset ones
      const mergedOptions = { ...presetOptions, ...(runtimeOptions || {}) };
      const mergedContext = { ...presetContext, ...(runTimeContext || {}) };
      const finalContent = runtimeContent || EduContent.toEduContent(presetContent as EduContentInterface);

      const handlerContext = this.contentActionDictionary[actionType];
      this.dispatchSideEffectActions(handlerContext, mergedContext, finalContent, ...extraArgs);

      originalHandler.call(opener, finalContent, mergedOptions, mergedContext, ...extraArgs);
    };
  }

  private dispatchSideEffectActions<K extends keyof ContentOpenerInterface>(
    handlerContext: ContentActionInterface,
    context: ContextInterface,
    ...actionArgs: Parameters<ContentOpenerInterface[K]>
  ) {
    const eduContent = actionArgs[0];
    if (!eduContent) return;

    const eduContentHasMinimal = 'minimal' in eduContent;

    this.store.dispatch(
      EventTrackingActions?.trackEduContentInteraction?.({
        action: 'edu_content_opened',
        elementId: 'edu-content-interaction',
        properties: {
          eduContent: eduContentHasMinimal ? eduContent.minimal : eduContent,
          handler: handlerContext,
          context,
        },
      })
    );
  }
}
